/** Copyright (c) 2025 DAIA Tech Pvt Ltd. All rights reserved.
 *
 * This software is confidential and proprietary information of DAIA Tech Pvt Ltd
 * and its licensors. You shall not disclose such Confidential Information and shall
 * use it only in accordance with the terms of the license agreement.
 *
 * Unauthorized reproduction, modification, distribution, or disclosure of this
 * software is strictly prohibited.
 */

import React from 'react';
import { Box, Menu, Portal, Icon } from 'chakra-v3';
import { Text } from '../../components/Text';
import { LuChevronDown } from 'react-icons/lu';
import { RiCheckboxLine } from 'react-icons/ri';

export interface DropdownOption {
  id: string;
  label: string;
  hasCheckbox?: boolean;
  isFirstItemWithBorder?: boolean;
}

interface DropdownProps {
  options: DropdownOption[];
  selectedValue: string;
  onSelect: (value: string) => void;
  placeholder?: string;
  width?: string | { base?: string; sm?: string; md?: string };
}

const Dropdown: React.FC<DropdownProps> = ({
  options,
  selectedValue,
  onSelect,
  placeholder,
  width = '304px',
}) => {
  const selectedOption = options.find((opt) => opt.id === selectedValue);
  const selectedLabel = selectedOption?.label || placeholder || 'Select an option';

  return (
    <Menu.Root>
      <Menu.Trigger asChild>
        <Box
          as="button"
          bg="#022a35"
          border="0.8px solid"
          borderColor="#166176"
          borderRadius="4px"
          px={4}
          py={2}
          h="48px"
          display="flex"
          alignItems="center"
          justifyContent="center"
          gap={2}
          cursor="pointer"
          _hover={{ borderColor: '#00afdf' }}
          _open={{ borderColor: '#00afdf' }}
          transition="border-color 0.2s"
          w={width}
        >
          <Text
            fontFamily="body"
            fontSize="16px"
            fontWeight="normal"
            lineHeight="24px"
            color="white"
            flex="1"
            textAlign="left"
          >
            {selectedLabel}
          </Text>
          <Icon fontSize="16px" color="white">
            <LuChevronDown />
          </Icon>
        </Box>
      </Menu.Trigger>
      <Portal>
        <Menu.Positioner>
          <Menu.Content
            bg="#002029"
            minW={width}
            borderRadius="8px"
            border="1px solid"
            borderColor="#166176"
            p={0}
            boxShadow="0px 4px 16px 0px rgba(0,0,0,0.16)"
          >
            {options.map((option, index) => {
              const isSelected = selectedValue === option.id;
              const hasCheckbox = option.hasCheckbox ?? false;
              const isFirstItemWithBorder = option.isFirstItemWithBorder ?? false;

              if (hasCheckbox) {
                return (
                  <React.Fragment key={option.id}>
                    {index > 0 && (
                      <Box h="0px" position="relative" w="100%">
                        <Box position="absolute" inset="-1px 0 0 0" borderTop="1px solid" borderColor="#055369" />
                      </Box>
                    )}
                    <Menu.CheckboxItem
                      value={option.id}
                      checked={isSelected}
                      onCheckedChange={(checked) => {
                        if (checked) {
                          onSelect(option.id);
                        }
                      }}
                      closeOnSelect={true}
                      bg="#002029"
                      color="#f6f6f6"
                      px={4}
                      py={3}
                      border={isFirstItemWithBorder ? '1px solid' : 'none'}
                      borderColor={isFirstItemWithBorder ? '#055369' : 'transparent'}
                      _hover={{ bg: 'rgba(0, 175, 223, 0.1)' }}
                      _checked={{ bg: '#002029' }}
                      display="flex"
                      alignItems="center"
                      gap={isSelected ? 2 : 0}
                    >
                      {isSelected && (
                        <Box
                          w="20px"
                          h="20px"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                          flexShrink={0}
                        >
                          <Icon fontSize="20px" color="#f6f6f6">
                            <RiCheckboxLine />
                          </Icon>
                        </Box>
                      )}
                      <Text
                        fontFamily="body"
                        fontSize="16px"
                        fontWeight="normal"
                        lineHeight="1.4"
                        letterSpacing="0.08px"
                        color="#f6f6f6"
                      >
                        {option.label}
                      </Text>
                    </Menu.CheckboxItem>
                  </React.Fragment>
                );
              }

              return (
                <React.Fragment key={option.id}>
                  {index > 0 && (
                    <Box h="0px" position="relative" w="100%">
                      <Box position="absolute" inset="-1px 0 0 0" borderTop="1px solid" borderColor="#055369" />
                    </Box>
                  )}
                  <Menu.Item
                    value={option.id}
                    onSelect={() => onSelect(option.id)}
                    closeOnSelect={true}
                    bg="#002029"
                    color="#f6f6f6"
                    px={4}
                    py={3}
                    _hover={{ bg: 'rgba(0, 175, 223, 0.1)' }}
                    display="flex"
                    alignItems="center"
                    gap={isSelected ? 2 : 0}
                  >
                    {isSelected && (
                      <Box w="20px" h="20px" display="flex" alignItems="center" justifyContent="center" flexShrink={0}>
                        <Icon fontSize="20px" color="#f6f6f6">
                          <RiCheckboxLine />
                        </Icon>
                      </Box>
                    )}
                    <Text
                      fontFamily="body"
                      fontSize="16px"
                      fontWeight="normal"
                      lineHeight="1.4"
                      letterSpacing="0.08px"
                      color="#f6f6f6"
                    >
                      {option.label}
                    </Text>
                  </Menu.Item>
                </React.Fragment>
              );
            })}
          </Menu.Content>
        </Menu.Positioner>
      </Portal>
    </Menu.Root>
  );
};

export default Dropdown;
